import { PeriodSelectorPresenter } from '../../../../../components/period';
import { MONITORING_SHORTCUTS } from '../monitoring.periods';
import { CubePresenter, Logger, NOOP_VOID } from 'wdc-cube';
import { reuseRows } from '../../../../../components/rows';
import { maskCpf } from '../../../../../domain';
import { MonitoringApi } from '../monitoring.api';
import { MonitoringDetailKeys } from '../detail/monitoring-detail.key';
import { buildCollaboratorColumns, buildCollaboratorRow, DEFAULT_SORT, MEASUREMENT_FIELDS } from '../../collaborators/collaborator-table';
import { sleepReportLinkOf } from '../../sleep-report/sleep-report.link';
import { MonitoringListKeys } from './monitoring-list.key';
import { MonitoringScope } from './monitoring-list.scope';
const BUSCA_DEBOUNCE_MS = 400;
const LOG = Logger.get('Mormaii.MonitoringListPresenter');
// @Inject
const api = MonitoringApi.INSTANCE;
/**
 * O criterio sem filtro NENHUM dos que a tela oferece.
 *
 * `companyId` nao entra aqui de proposito: ele nao e filtro desta tela, e sim o contexto
 * em que ela vive - o seletor dele fica no cabecalho. Quem limpa os filtros continua na
 * mesma empresa, e por isso o `onClearFilters` o carrega a parte.
 */
const UNFILTERED = {};
/** O que a tela conta como "tem filtro ligado" - a empresa do cabecalho nao conta. */
const FILTROS_DA_TELA = ['name', 'cpf', 'periodStart', 'periodEnd'];
/**
 * Monitoramentos compartilhados: os pacientes que autorizaram este profissional.
 *
 * A tabela e a mesma da lista de colaboradores, montada pelas funcoes de
 * `collaborator-table` - as 14 colunas, o formato de cada valor e o criterio de
 * ordenacao nao mudam porque a populacao mudou.
 *
 * O que muda e o endpoint e quem responde por ele: aqui e
 * `/v2/panel-patients/attended/search`, que ja devolve so o que o
 * profissional logado pode ver. Nao ha filtro de permissao no cliente, e nao
 * deveria haver.
 */
export class MonitoringListPresenter extends CubePresenter {
    /**
     * O modelo da tabela - do CONCEITO, que sobrevive a ida ao detalhe. A listagem sempre
     * esta debaixo dele na arvore; montada sem ele, a fiacao esta quebrada, e falhar alto
     * e melhor que criar em silencio um segundo modelo, sem a escolha gravada antes.
     */
    get table() {
        if (!this.concept) {
            throw new Error('A listagem de Monitoramento sem o conceito acima dela');
        }
        return this.concept.table;
    }
    constructor(app) {
        super(app, new MonitoringScope());
        this.parentSlot = NOOP_VOID;
        /** Quem publica o filtro no endereco - ver `MonitoringPresenter`. */
        /**
         * O periodo ESCOLHIDO, esperando o "Aplicar busca".
         *
         * O seletor avisa a cada escolha, mas esta tela e um formulario de busca com botao:
         * aplicar na hora faria o periodo valer sozinho enquanto nome e CPF esperam o envio,
         * e o mesmo cartao teria dois comportamentos. Entao a escolha fica guardada aqui e
         * sobe junto com o resto.
         */
        this.escolhido = { from: '', to: '' };
        this.period = new PeriodSelectorPresenter(this.update, MONITORING_SHORTCUTS, async ({ from, to, shortcut }) => {
            this.escolhido = { from, to };
            /*
             * O `show` e obrigatorio aqui, e foi o que faltou na primeira versao: o
             * seletor AVISA a escolha, mas quem desenha o resultado dela - o rotulo do
             * menu, a marca na opcao, as duas datas - e o `show`.
             *
             * Nas outras quatro telas ele vem de graca: la o `onChange` chama `go`, o
             * endereco muda, e o `showRequest` chama `show` na volta. Aqui a escolha
             * espera o "Aplicar busca" e nao passa pelo endereco, entao ninguem chamava
             * - e trocar o item do combo nao fazia nada na tela.
             */
            this.period.show(shortcut, from, to);
            // Escolha discreta, sem espera: nao ha o que "parar de digitar".
            await this.aplicar();
        });
        this.at = { criteria: {}, page: 1 };
        this.all = [];
        this.pending = 0;
    }
    async applyParameters(intent, initialization) {
        const keys = new MonitoringListKeys(this.app, intent);
        // A guarda de papel esta no CONCEITO, ancestral desta tela e do detalhe.
        this.concept = keys.concept;
        // Atributos da intencao: lidos a cada chamada, como os slots. Vem de quem esta
        // acima na composicao, e reler custa nada.
        this.owner = keys.owner;
        if (initialization) {
            this.scope.onNameChanged = this.handleTextChanged.bind(this, 'name');
            this.scope.onCpfChanged = this.handleTextChanged.bind(this, 'cpf');
            this.scope.period = this.period.scope;
            this.scope.onSubmit = this.action(this.onSubmit);
            this.scope.onClearFilters = this.action(this.onClearFilters);
            this.scope.onPreviousPage = this.action(this.onMovePage.bind(this, -1));
            this.scope.onNextPage = this.action(this.onMovePage.bind(this, 1));
            this.scope.onGoToPage = this.action(this.onGoToPage);
            LOG.info('Initialized');
            // As sugestoes nao sao esperadas: a tabela nao depende delas, e os dois
            // endpoints estao quebrados no backend (ver o servico).
            void this.loadSuggestions();
        }
        // O slot e decidido acima na composicao e pode mudar entre viagens: le-se sempre.
        this.parentSlot = keys.parentSlot;
        this.owner?.showSection('monitoring', 'Monitoramentos Compartilhados', 'Lista de colaboradores que compartilharam seus monitoramentos com você');
        this.parentSlot(this.scope);
        this.scope.layout = this.table.show(this);
        /*
         * A empresa em vigor no cabecalho, quando ela e ATENDIDA - ver
         * `fetchSharedPatients` e `RestrictedPresenter.companyServedInScope`.
         *
         * O profissional so alcanca colaborador de empresa em que esta lotado, e
         * trocar de empresa ali refaz esta lista: a troca e uma VIAGEM, e este metodo
         * roda de novo com a empresa nova.
         *
         * `companyInScope` NAO serve aqui: para quem tambem administra, ele devolve
         * empresa administrada, e o servidor recusa com 400.
         */
        const empresa = this.owner?.companyServedInScope;
        // Trocar de empresa volta para a PRIMEIRA pagina - ver `CollaboratorsPresenter`.
        const trocouDeEmpresa = this.at.criteria.companyId !== undefined && this.at.criteria.companyId !== empresa;
        await this.moveTo({
            criteria: {
                name: keys.name,
                cpf: keys.cpf,
                periodStart: keys.periodStart,
                periodEnd: keys.periodEnd,
                companyId: empresa
            },
            sort: keys.sort,
            page: trocouDeEmpresa ? 1 : keys.page
        });
        return true;
    }
    /*
     * Sem `publishParameters`: os nomes sao do CONCEITO, dono unico deles. Ver
     * `CollaboratorListPresenter`, que segue o mesmo desenho.
     */
    async go(change) {
        await this.moveTo({ ...this.at, ...change });
        this.concept?.moveTo(this.at);
        this.updateHistory();
    }
    async moveTo(target) {
        this.showRequest(target);
        const signature = JSON.stringify(target.criteria);
        if (signature !== this.lastQuery) {
            const token = ++this.pending;
            this.scope.loading = true;
            try {
                const page = await api.fetchSharedPatients(target.criteria);
                if (token !== this.pending) {
                    return;
                }
                this.lastQuery = signature;
                this.all = page.elements ?? [];
            }
            catch (caught) {
                if (token !== this.pending) {
                    return;
                }
                // Mensagem e botao sao os de la, e o aviso e o modal global - a tela
                // nao mostra erro dentro dela.
                LOG.warn('Could not load the shared monitorings', caught);
                this.all = [];
                this.app.alert('error', 'Erro', 'Não foi possível buscar os colaboradores');
                this.at = target;
                this.showRequest(target);
                this.showRows(target);
                this.scope.loading = false;
                return;
            }
            finally {
                if (token === this.pending) {
                    this.scope.loading = false;
                }
            }
        }
        this.at = target;
        this.showRows(target);
    }
    async loadSuggestions() {
        const [names, cpfs] = await Promise.all([
            api.fetchUserNames().catch((caught) => {
                LOG.warn('Could not load the name suggestions', caught);
                return [];
            }),
            api.fetchUserCpfs().catch((caught) => {
                LOG.warn('Could not load the CPF suggestions', caught);
                return [];
            })
        ]);
        this.scope.nameOptions = names;
        this.scope.cpfOptions = cpfs;
    }
    showRequest(target) {
        const criteria = target.criteria;
        /*
         * So escreve o que veio de FORA - ver `aplicar`. Reescrever o que a propria
         * pessoa digitou devolveria o campo ao valor anterior no meio da digitacao.
         */
        if (criteria.name !== this.enviado?.name) {
            this.scope.name = criteria.name ?? '';
            this.scope.formRevision++;
        }
        if (criteria.cpf !== this.enviado?.cpf) {
            this.scope.cpf = criteria.cpf ?? '';
            this.scope.formRevision++;
        }
        /*
         * O periodo, pela mesma regra dos campos de texto: so se reescreve o que veio de
         * FORA.
         *
         * O atalho nao viaja no endereco - so as duas datas -, entao reconstruir o
         * seletor a partir delas trocaria "Ultimos 30 dias" por "Escolher datas" no
         * instante seguinte a escolha. Vindo de fora (o Voltar, um link colado), "Escolher
         * datas" e a leitura honesta: o atalho era so o caminho ate aquelas duas datas.
         */
        const periodoDeFora = 
        // Na primeira abertura nada foi enviado, e o seletor precisa nascer desenhado.
        !this.enviado ||
            criteria.periodStart !== this.enviado.periodStart ||
            criteria.periodEnd !== this.enviado.periodEnd;
        if (periodoDeFora) {
            this.escolhido = { from: criteria.periodStart ?? '', to: criteria.periodEnd ?? '' };
            if (criteria.periodStart && criteria.periodEnd) {
                this.period.show('custom', criteria.periodStart, criteria.periodEnd);
            }
            else {
                this.period.show('all');
            }
        }
        this.scope.cpfError = undefined;
        this.buildColumns(target.sort);
        this.scope.anyFilterActive = FILTROS_DA_TELA.some((key) => criteria[key] !== UNFILTERED[key]);
    }
    /**
     * As colunas, com as medicoes na ordem que a pessoa arrastou - como na lista de
     * Colaboradores. As fixas (atencao, nome, origem e atualizacao) identificam a linha
     * e nao se movem. A ordem e preferencia de quem le: nao vai para o endereco.
     */
    buildColumns(sort) {
        this.scope.columns = buildCollaboratorColumns(sort, this.update, (field) => this.onSort(field), {
            measurements: this.table.preferences.arrange(MEASUREMENT_FIELDS),
            onDrop: (source, target) => {
                if (this.table.preferences.move(MEASUREMENT_FIELDS, source, target)) {
                    this.buildColumns(this.at.sort);
                }
            }
        }, this.scope.columns);
        this.table.setVisible(this.scope.columns.map((column) => column.field));
    }
    showRows(target) {
        this.table.setSort(target.sort ?? DEFAULT_SORT);
        const ordered = this.table.sortRows(this.all);
        this.scope.total = ordered.length;
        const pageCount = Math.max(1, Math.ceil(ordered.length / this.scope.perPage));
        const page = Math.min(Math.max(1, target.page), pageCount);
        if (page !== target.page) {
            this.at = { ...target, page };
        }
        this.scope.page = page;
        const start = (page - 1) * this.scope.perPage;
        // As linhas que ja estao na tela sao atualizadas, e nao recriadas - ver `reuseRows`.
        this.scope.rows = reuseRows(this.scope.rows, ordered.slice(start, start + this.scope.perPage), (patient) => patient.patientId, (patient, reuse) => {
            // O detalhe HOSPEDADO AQUI, e nao o de Colaboradores: sao dois
            // places, e o "Voltar" de cada um cai na lista que o hospeda.
            const href = this.app.sealedAddress(MonitoringDetailKeys.plainAddressOf(patient.patientId));
            // A celula de sono leva ao Relatorio de Sono da pessoa - ver `sleepReportLinkOf`.
            const link = sleepReportLinkOf(this.app, this.owner, patient);
            const sleepLink = link && { ...link, open: this.action(link.open) };
            return buildCollaboratorRow(patient, this.update, () => this.onOpen(patient), href, reuse, sleepLink);
        });
    }
    // ========== ACOES ==========
    /**
     * Espelha o digitado, e agenda a busca.
     *
     * So NOME e CPF passam por aqui. As duas datas tinham mascara propria ate
     * 18/09/2026, quando o periodo passou a usar o seletor que as outras quatro telas
     * usam - e com ele nao ha texto livre a mascarar.
     *
     * A busca sai depois de a pessoa PARAR de digitar. Sem a espera seriam sete
     * consultas para escrever "Alexandre", e o endereco ganharia sete entradas no
     * historico - o Voltar passaria a desfazer letra por letra.
     */
    handleTextChanged(field, value) {
        this.scope[field] = field === 'cpf' ? maskCpf(value) : value;
        if (!this.validate()) {
            return;
        }
        clearTimeout(this.espera);
        this.espera = setTimeout(() => void this.aplicar(), BUSCA_DEBOUNCE_MS);
    }
    /**
     * Manda o que esta na tela para o endereco, e com ele para o servidor.
     *
     * `enviado` guarda o que subiu, e existe por causa de uma corrida: os campos de
     * texto sao nao controlados, e o `showRequest` os reescreve quando a resposta
     * volta. Digitar mais uma letra durante a ida faria o campo VOLTAR para o valor
     * anterior, com o cursor no fim. Comparando com o que foi enviado, o `showRequest`
     * so escreve quando a mudanca veio de FORA - o Voltar, um link colado, o Limpar.
     */
    async aplicar() {
        clearTimeout(this.espera);
        const criteria = {
            name: this.scope.name || undefined,
            cpf: this.scope.cpf || undefined,
            periodStart: this.escolhido.from || undefined,
            periodEnd: this.escolhido.to || undefined,
            /*
             * A empresa do cabecalho vai JUNTO, e nao e detalhe.
             *
             * Ela nao esta na tela - o seletor dela fica no cabecalho -, e por isso o
             * antigo "Aplicar busca" a deixava cair: montava o criterio so com o que os
             * campos diziam. O efeito era filtrar por nome e receber colaborador de outra
             * empresa, porque `companyId` nulo pede ao servidor uma lista mais larga.
             * Foi como este defeito apareceu, em 18/09/2026.
             */
            companyId: this.at.criteria.companyId
        };
        this.enviado = criteria;
        await this.go({ criteria, page: 1 });
    }
    /**
     * As regras do `schema` da referencia, com o texto dela.
     *
     * Um CPF comecado e nao terminado vira "Selecione um CPF"; data que nao existe
     * ou no futuro vira "Data inválida"; e inicio depois do fim marca os DOIS
     * campos.
     */
    validate() {
        const digits = this.scope.cpf.replace(/\D/g, '');
        this.scope.cpfError = digits.length > 0 && digits.length < 11 ? 'Selecione um CPF' : undefined;
        /*
         * As datas deixaram de ser validadas aqui: o seletor so avisa a mudanca quando as
         * duas pontas formam um dia completo e o inicio nao passa do fim. As tres
         * mensagens que existiam - data incompleta, data no futuro e inicio depois do fim
         * - viravam estado impossivel de alcancar pelo componente.
         */
        return !this.scope.cpfError;
    }
    /**
     * O Enter, que adianta a espera.
     *
     * O botao de aplicar saiu em 18/09/2026 - o filtro passou a valer sozinho -, mas o
     * `<form>` continua submetendo no Enter, e quem aperta nao quer esperar os 400ms.
     */
    async onSubmit() {
        if (!this.validate()) {
            return;
        }
        await this.aplicar();
    }
    /**
     * Limpar zera os campos AQUI, e nao espera o `showRequest`.
     *
     * Ele nao os tocaria: o criterio limpo e exatamente o que acabou de ser enviado, e a
     * guarda contra a corrida existe para nao reescrever o que a pessoa digitou.
     */
    async onClearFilters() {
        clearTimeout(this.espera);
        this.scope.name = '';
        this.scope.cpf = '';
        this.scope.cpfError = undefined;
        this.scope.formRevision++;
        this.escolhido = { from: '', to: '' };
        this.period.show('all');
        // A empresa do cabecalho NAO e filtro desta tela: limpar nao muda de empresa.
        const limpo = { ...UNFILTERED, companyId: this.at.criteria.companyId };
        this.enviado = limpo;
        await this.go({ criteria: limpo, page: 1 });
    }
    async onSort(field) {
        const current = this.at.sort;
        const sort = current === field ? `-${field}` : current === `-${field}` ? undefined : field;
        await this.go({ sort, page: 1 });
    }
    /** Ir direto a uma pagina - o paginador numerado da referencia tem isso. */
    async onGoToPage(page) {
        const alvo = Math.min(Math.max(1, page), this.scope.pageCount);
        if (alvo !== this.at.page) {
            await this.go({ page: alvo });
        }
    }
    async onMovePage(direction) {
        const page = Math.min(Math.max(1, this.at.page + direction), this.scope.pageCount);
        if (page !== this.at.page) {
            await this.go({ page });
        }
    }
    /**
     * Abre o detalhe do paciente, na montagem DESTA tela.
     *
     * E o mesmo apresentador que a lista de Colaboradores abre - um paciente e um
     * paciente -, mas noutro ponto da arvore: `monitoring/detail` em vez de
     * `collaborators/detail`. A diferenca aparece no Voltar, que cai aqui, e no
     * fato de esta lista continuar VIVA enquanto o detalhe esta aberto: os filtros,
     * a pagina e a ordenacao seguem publicados no endereco e a volta e instantanea.
     *
     * As keys sao as DESTA montagem, e estendem as desta lista - por isso o place e
     * constante e a viagem parte de `of(app)` como em qualquer outra tela.
     */
    async onOpen(patient) {
        const target = MonitoringDetailKeys.of(this.app);
        target.patientId = patient.patientId;
        target.row = patient;
        await target.flip();
    }
}
//# sourceMappingURL=monitoring-list.presenter.js.map